import React from "react";
import { View, Text, Platform, Pressable, TextStyle } from "react-native";
import {
  currencyFormatter,
  dateFormatter,
  getFormattedLabelText,
} from "@lib/utilities/Formatters";
import PaymentsList from "../../components/billing/PaymentsList";
import { createPlatformStyles, theme } from "../../theme/theme";
import InfoListSection from "../common/InfoListSection";
import { getPaymentInfo } from "@lib/utilities/HelperFunctions";
import { messages } from "../../resources/strings";
import { labelText } from "../../resources/strings";

interface BillingContentProps {
  isPayNowVisible: boolean;
  onPaymentButtonPress: any;
  billingDetails: any;
  paymentsArray: any[];
  expandedIndex: number;
  toggleExpandedView: (index: number) => void;

  getTotalPaymentAmount: () => number;
}

const BillingContent = ({
  isPayNowVisible,
  onPaymentButtonPress,
  billingDetails,
  paymentsArray,
  expandedIndex,
  toggleExpandedView,
  getTotalPaymentAmount,
}: BillingContentProps) => {
  return (
    <View style={[styles.container]} dataSet={{ media: ids.container }}>
      <View style={styles.docInfoCtr} dataSet={{ media: ids.docInfoCtr }}>
        <View
          style={[styles.paymentHeader]}
          dataSet={{ media: ids.paymentHeader }}
        >
          {billingDetails.premium?.isDue ? (
            <>
              <Text
                style={styles.paymentOf}
                dataSet={{ media: ids.paymentOf }}
                testID="billing-page-payment-label"
              >
                Payment of{" "}
                {currencyFormatter(
                  getPaymentInfo(
                    billingDetails?.information.nextPayment?.value,
                    billingDetails.premium.balanceDue?.value,
                  ),
                )}{" "}
                {billingDetails?.information.nextPayment?.value == 0
                  ? `is due.`
                  : `due on`}{" "}
                {billingDetails?.information.nextPayment?.value > 0 &&
                  dateFormatter(billingDetails?.information.paymentDue?.value)}
              </Text>
              {isPayNowVisible ? (
                <Pressable
                  onPress={onPaymentButtonPress}
                  style={styles.payNowBtn}
                  testID={"billing-page-pay-now-button"}
                >
                  <Text style={styles.whiteText}>Pay Now</Text>
                </Pressable>
              ) : (
                <Text style={[styles.paymentOf, styles.mt]}>
                  {messages.payments.maintenance}
                </Text>
              )}
            </>
          ) : (
            <Text
              style={styles.paymentOf}
              dataSet={{ media: ids.paymentOf }}
              testID="billing-page-no-due-label"
            >
              {labelText.billing.noPaymentsDue}
            </Text>
          )}
        </View>

        <View style={[styles.claimDetail]} dataSet={{ media: ids.claimDetail }}>
          <>
            {Object.keys(billingDetails).map((key) => {
              return (
                <InfoListSection
                  listStyle={{ marginBottom: theme.margin.reg }}
                  key={key + "billing-section"}
                  title={getFormattedLabelText(key)}
                  items={billingDetails[key]}
                  page="billing"
                />
              );
            })}
          </>

          <PaymentsList
            paymentsArray={paymentsArray}
            expandedIndex={expandedIndex}
            toggleExpandedView={toggleExpandedView}
            getTotalPaymentAmount={getTotalPaymentAmount}
          />
        </View>
      </View>
    </View>
  );
};

export default BillingContent;

const { styles, ids } = createPlatformStyles({
  mobile: {
    mt: { marginVertical: theme.margin.xlg },
    payNowBtn: {
      marginTop: theme.margin.cs,
      alignSelf: "center",
      paddingVertical: theme.spacing.ssm,
      paddingHorizontal: theme.spacing.xxl,
      color: theme.colors.white,
      backgroundColor: theme.colors.button.payNow,
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      borderRadius: theme.radius.xxs,
    },
    whiteText: {
      textAlign: "center",
      color: theme.colors.white,
      fontWeight: theme.fontweight.light as TextStyle["fontWeight"],
      fontSize: theme.fontsize.mmd,
    },
    container: {
      marginTop: theme.margin.reg,
      marginBottom: theme.margin.lg,
      marginLeft: "auto",
      marginRight: "auto",
      "@media (min-width: 385px)": {
        width: "100%",
      },
      "@media (min-width: 900px)": {
        width: 600,
      },
      "@media (min-width: 1000px)": {
        width: 600,
      },
      "@media (min-width: 1200px)": {
        width: 600,
      },
      ...Platform.select({
        ios: {
          marginTop: theme.margin.xxs,
          "@media (min-width: 200px)": {
            width: "100%",
            paddingHorizontal: theme.spacing.md,
          },
          "@media (min-width: 700px)": {
            width: 600,
          },
        },
        android: {
          "@media (min-width: 200px)": {
            width: "100%",
            paddingHorizontal: theme.spacing.md,
            marginTop: theme.margin.xxs,
          },
          "@media (min-width: 700px)": {
            width: 600,
            marginTop: theme.margin.reg,
          },
        },
      }),
    },
    docInfoCtr: {
      width: "100%",
      position: "relative",

      minWidth: "100%",
      marginLeft: "auto",
      marginRight: "auto",
      flex: 1,
    },
    paymentHeader: {
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      marginBottom: theme.margin.mmd,
    },
    paymentOf: {
      marginTop: theme.margin.reg,
      color: theme.colors.white,
      fontFamily: theme.fontFamily.robotoSlabMedium,

      fontSize: theme.fontsize.md,
      textAlign: "center",
    },
    claimDetail: {
      zIndex: theme.zIndex.min,
    },
  },
  web: {
    mt: { marginVertical: theme.margin.xlg },
    payNowBtn: {
      marginVertical: theme.margin.md,
      marginTop: theme.margin.xlg,
      paddingHorizontal: theme.spacing.xxl,
      alignSelf: "center",
      paddingVertical: theme.spacing.ssm,
      color: theme.colors.white,
      backgroundColor: theme.colors.button.payNow,
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      borderRadius: theme.radius.xxs,
    },
    whiteText: {
      justifyContent: "center",
      color: theme.colors.white,
      fontWeight: theme.fontweight.light as TextStyle["fontWeight"],
      fontSize: theme.fontsize.mmd,
    },
    container: {
      marginTop: theme.margin.reg,
      marginBottom: theme.margin.lg,
      marginLeft: "auto",
      marginRight: "auto",
      maxWidth: 1180,
      "@media (min-width: 200px)": {
        width: "90%",
      },
      "@media (min-width: 700px)": {
        maxWidth: 1180,
      },
      "@media (min-width: 1000px)": {
        maxWidth: 1180,
        marginLeft: "auto",
        marginRight: "auto",
        paddingHorizontal: theme.spacing.md,
        width: "100%",
      },
    },
    docInfoCtr: {
      width: "100%",
      position: "relative",

      "@media (min-width: 200px)": {
        width: "100%",
      },
      "@media (min-width: 700px)": {
        width: "70%",
      },
      marginLeft: "auto",
      marginRight: "auto",
      flex: 1,
    },
    paymentHeader: {
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      marginBottom: theme.margin.mmd,
    },
    paymentOf: {
      marginTop: theme.margin.reg,
      color: theme.colors.white,
      fontFamily: theme.fontFamily.robotoSlabMedium,
      fontWeight: theme.fontweight.bold as any,
      fontSize: theme.fontsize.md,
      textAlign: "center",
    },
    claimDetail: {
      zIndex: theme.zIndex.min,
    },
  },
});
